import React, { useState } from "react";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { useMetrics, useCampaigns, useAccounts, useSortableData } from "../hooks/useAdData";

const GOOGLE_COLOR = "#4F9EF8";
const META_COLOR   = "#8B78FF";

function fmt(n, prefix = "") {
  if (n == null) return "—";
  if (n >= 10000000) return `${prefix}${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `${prefix}${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)     return `${prefix}${(n / 1000).toFixed(1)}K`;
  return `${prefix}${Number(n).toFixed(0)}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#1C1E27", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
      <div style={{ color: "#8A8BA0", marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color, fontFamily: "DM Mono, monospace" }}>
          {p.name}: ₹{Number(p.value).toLocaleString()}
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [selectedAccount, setSelectedAccount] = useState("");  // "" = All
  const [selectedPlatform, setSelectedPlatform] = useState("");

  const { accounts, loading: accountsLoading } = useAccounts();
  const { data: metrics } = useMetrics(selectedAccount, selectedPlatform);
  const { campaigns: rawCampaigns, loading: campLoading, lastSynced, sync } = useCampaigns(
    selectedPlatform,
    "LAST_30_DAYS",
    selectedAccount
  );
  const [syncing, setSyncing] = useState(false);
  const { sorted: campaigns, sortKey, sortDir, requestSort } = useSortableData(rawCampaigns, "spend", "desc");

  const combined = metrics?.combined || {};
  const google   = metrics?.google   || {};
  const meta      = metrics?.meta     || {};

  const handleSync = async () => {
    setSyncing(true);
    await sync();
    setSyncing(false);
  };

  const handleAccountSelect = (accountId, platform) => {
    setSelectedAccount(accountId);
    setSelectedPlatform(platform);
  };

  const pieData = [
    { name: "Google", value: google.spend || 0 },
    { name: "Meta",   value: meta.spend   || 0 },
  ].filter(d => d.value > 0);

  // Build spend-by-account bar data from campaigns
  const accountSpend = {};
  campaigns.forEach(c => {
    const key = c.accountName || c.accountId || "Unknown";
    accountSpend[key] = (accountSpend[key] || 0) + (c.spend || 0);
  });
  const spendByAccount = Object.entries(accountSpend)
    .map(([name, spend]) => ({ name: name.length > 18 ? name.slice(0, 17) + "…" : name, spend }))
    .sort((a, b) => b.spend - a.spend)
    .slice(0, 8);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">
            {selectedAccount
              ? `${accounts.find(a => String(a.id) === selectedAccount)?.name || selectedAccount} · `
              : "All accounts · "}
            {lastSynced
              ? `Last synced: ${new Date(lastSynced).toLocaleTimeString()}`
              : "Fetching data…"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn btn-ghost" onClick={handleSync} disabled={syncing}>
            {syncing ? "Syncing…" : "↻  Sync now"}
          </button>
        </div>
      </div>

      {/* ── Account Switcher ── */}
      <div className="page-body" style={{ paddingTop: 0 }}>
        <div style={{ marginBottom: 20 }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
              Ad Accounts
            </span>
            {selectedAccount && (
              <button
                onClick={() => { setSelectedAccount(""); setSelectedPlatform(""); }}
                style={{ fontSize: 11, color: "var(--text3)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}
              >
                Clear filter
              </button>
            )}
          </div>

          {/* Chips row */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <AccountChip
              label="All Accounts"
              platform={null}
              active={!selectedAccount}
              onClick={() => { setSelectedAccount(""); setSelectedPlatform(""); }}
              count={accounts.length}
            />

            {accountsLoading ? (
              <span style={{ fontSize: 12, color: "var(--text3)", alignSelf: "center" }}>Discovering accounts…</span>
            ) : accounts.length === 0 ? (
              <span style={{ fontSize: 12, color: "var(--amber)", alignSelf: "center" }}>
                No accounts found — click Sync
              </span>
            ) : (
              <>
                {/* Group by platform */}
                {["google", "meta"].map(platform => {
                  const platformAccounts = accounts.filter(a => a.platform === platform);
                  if (!platformAccounts.length) return null;
                  return (
                    <React.Fragment key={platform}>
                      <span style={{ fontSize: 11, color: "var(--text3)", alignSelf: "center", margin: "0 4px" }}>
                        {platform === "google" ? "G" : "M"}:
                      </span>
                      {platformAccounts.map(a => (
                        <AccountChip
                          key={`${a.platform}-${a.id}`}
                          label={a.name}
                          platform={a.platform}
                          active={selectedAccount === String(a.id)}
                          onClick={() => handleAccountSelect(String(a.id), a.platform)}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {/* ── KPIs ── */}
        <div className="kpi-grid">
          <KPICard label="Total Spend"   value={fmt(combined.spend, "₹")}   accent="#5B7FFF" />
          <KPICard label="Total Clicks"  value={fmt(combined.clicks)}         accent="#4F9EF8" />
          <KPICard label="Avg ROAS"      value={combined.roas ? `${combined.roas.toFixed(2)}×` : "—"} accent="#8B78FF" />
          <KPICard label="Conversions"   value={fmt(combined.conversions)}    accent="#3DD68C" />
        </div>

        {/* ── Charts ── */}
        <div className="charts-row">
          {/* Platform split pie */}
          <div className="card">
            <div className="card-title">Spend by Platform</div>
            {pieData.length === 0 ? (
              <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 13 }}>
                No spend data — sync to load
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.name === "Google" ? GOOGLE_COLOR : META_COLOR} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `₹${Number(v).toLocaleString()}`} contentStyle={{ background: "#1C1E27", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                  <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ color: "#8A8BA0", fontSize: 12 }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Spend by account bar */}
          <div className="card">
            <div className="card-title">Spend by Account</div>
            {spendByAccount.length === 0 ? (
              <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 13 }}>
                No data — sync to load
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={spendByAccount} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <XAxis type="number" tick={{ fill: "#55566A", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => fmt(v, "₹")} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#8A8BA0", fontSize: 10 }} axisLine={false} tickLine={false} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="spend" name="Spend" fill={GOOGLE_COLOR} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Campaigns table ── */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>
            Campaigns
            {selectedAccount && <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: 8, fontWeight: 400 }}>filtered by account</span>}
          </div>
          {campLoading ? (
            <div className="spinner" />
          ) : campaigns.length === 0 ? (
            <p style={{ color: "var(--text3)", textAlign: "center", padding: "30px 0" }}>
              No campaigns found — hit "Sync now" to pull live data.
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  {[
                    { key: "name",        label: "Campaign" },
                    { key: "accountName", label: "Account" },
                    { key: "platform",    label: "Platform" },
                    { key: "spend",       label: "Spend" },
                    { key: "dailyBudget", label: "Budget/day" },
                    { key: "roas",        label: "ROAS" },
                    { key: "clicks",      label: "Clicks" },
                    { key: "conversions", label: "Conv." },
                    { key: "convRate",    label: "Conv. Rate" },
                    { key: "ctr",         label: "CTR" },
                    { key: "status",      label: "Status" },
                  ].map(col => (
                    <th key={col.key} onClick={() => requestSort(col.key)} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                      {col.label} {sortKey === col.key ? (sortDir === "asc" ? "▲" : "▼") : <span style={{ opacity: 0.3 }}>⇅</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id}>
                    <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</td>
                    <td style={{ fontSize: 12, color: "var(--text3)" }}>{c.accountName || "—"}</td>
                    <td><span className={`badge badge-${c.platform}`}>{c.platform}</span></td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>₹{Number(c.spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
                      {c.dailyBudget > 0 ? `₹${Number(c.dailyBudget).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
                    </td>
                    <td style={{ fontFamily: "var(--mono)", color: c.roas >= 3 ? "var(--green)" : c.roas >= 2 ? "var(--amber)" : "var(--red)" }}>
                      {Number(c.roas || 0).toFixed(2)}×
                    </td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{Number(c.clicks || 0).toLocaleString()}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{Number(c.conversions || 0).toFixed(1)}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{Number(c.convRate || 0).toFixed(2)}%</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{Number(c.ctr || 0).toFixed(2)}%</td>
                    <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountChip({ label, platform, active, onClick, count }) {
  const color = platform === "google" ? GOOGLE_COLOR : platform === "meta" ? META_COLOR : "#5B7FFF";
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 20,
        border: `1px solid ${active ? color : "rgba(255,255,255,0.1)"}`,
        background: active ? `${color}18` : "transparent",
        color: active ? color : "var(--text2)",
        fontSize: 12,
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "all 0.15s",
        whiteSpace: "nowrap",
      }}
    >
      {platform && (
        <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: platform === "google" ? GOOGLE_COLOR : META_COLOR,
          flexShrink: 0,
        }} />
      )}
      {label}
      {count != null && (
        <span style={{ opacity: 0.5, fontSize: 10, marginLeft: 2 }}>({count})</span>
      )}
    </button>
  );
}

function KPICard({ label, value, accent }) {
  return (
    <div className="kpi-card" style={{ "--accent-line": accent }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}
